import { FunctionComponent, useEffect, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import InputsNew from "../common/InputsNew";
import SearchableInputsDropdown from "../common/SearchableDropdown";
import { showError, showSuccess } from "../common/ToastNotification";
import { AGENCY_PROGRESS_STATUS, EMPLOYEES_BY_AGENCY_ID, MIGRATION_AGENCY_BY_ID } from "../../api/query";
import { CREATE_MIGRATION_AGENCY, SYNC_AGENCY_FIELD_STATUS, SYNC_ENTITIES_FIELD_STATUS, SYNC_ENTITIES_NEW } from "../../api/mutation";
import Loader from "../common/Loader";
import { useAuth } from "../../context/AuthContext";

interface AgencyConfigTabProps {
    agencyId: any;
    onSaveSuccess?: () => void;
    onSyncSuccess?: () => void;
}

interface FormValues {
    agency_name: string;
    new_agency_id: string;
    old_agency_id?: string;
    total_clients: number;
    total_caregivers: number;
    total_employees: number;
    total_prospects?: number;
    verification_working_days: number;
    onboarding_date?: string;
    verification_start_date?: string;
    data_migration_date?: string;
}

const schema = yup.object().shape({
    agency_name: yup.string()
        .test("no-whitespace", "This field is required", v => !!v && v.trim().length > 0)
        .required("This field is required"),
    new_agency_id: yup.string().required("This field is required"),
    old_agency_id: yup.string().nullable(),
    total_clients: yup.number().typeError("Must be a number").min(0).required("This field is required"),
    total_caregivers: yup.number().typeError("Must be a number").min(0).required("This field is required"),
    total_employees: yup.number().typeError("Must be a number").min(0).required("This field is required"),
    total_prospects: yup.number().typeError("Must be a number").min(0).nullable(),
    verification_working_days: yup.number().typeError("Must be a number").min(0).required("This field is required"),
    onboarding_date: yup.string().nullable(),
    verification_start_date: yup.string().nullable(),
    data_migration_date: yup.string().nullable(),
});

const AgencyConfigTab: FunctionComponent<AgencyConfigTabProps> = ({ agencyId, onSaveSuccess, onSyncSuccess }) => {
    const { userInfo } = useAuth();
    const employeeID = userInfo?.employee_id || 0;
    const organizationID = userInfo?.agencies?.id || 0;

    const [progressStatusOptions, setProgressStatusOptions] = useState<{ key: string; name: string }[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [selectedStatusLabel, setSelectedStatusLabel] = useState<string>("");

    const [employeeOptions, setEmployeeOptions] = useState<{ key: number; name: string; initials: string }[]>([]);
    const [assignedMemberIds, setAssignedMemberIds] = useState<number[]>([]);
    const [memberSearch, setMemberSearch] = useState("");

    const [cxMember1, setCxMember1] = useState<string>("");
    const [cxMember1Label, setCxMember1Label] = useState<string>("");
    const [cxMember2, setCxMember2] = useState<string>("");
    const [cxMember2Label, setCxMember2Label] = useState<string>("");
    const [agencyType, setAgencyType] = useState<string>("");
    const [agencyTypeError, setAgencyTypeError] = useState<string>("");

    const [synced, setSynced] = useState(false);
    const [syncing, setSyncing] = useState(false);

    // Queries
    const { data: agencyData, loading: agencyLoading } = useQuery(MIGRATION_AGENCY_BY_ID, {
        variables: { agency_id: parseInt(agencyId) },
        fetchPolicy: "no-cache",
        skip: !agencyId,
    });

    const { data: statusData } = useQuery(AGENCY_PROGRESS_STATUS, { fetchPolicy: "no-cache" });

    const [getEmployees, { data: employeesData, loading: employeesLoading }] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
        fetchPolicy: "no-cache",
    });

    const [updateAgency, { loading: saving }] = useMutation(CREATE_MIGRATION_AGENCY, {
        fetchPolicy: "no-cache",
    });

    const [syncAgencyFieldStatus] = useMutation(SYNC_AGENCY_FIELD_STATUS, { fetchPolicy: "no-cache" });
    const [syncEntities] = useMutation(SYNC_ENTITIES_NEW, { fetchPolicy: "no-cache" });
    const [syncEntitiesFieldStatus] = useMutation(SYNC_ENTITIES_FIELD_STATUS, { fetchPolicy: "no-cache" });

    const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm<FormValues>({
        resolver: yupResolver(schema) as any,
    });

    // Load employees
    useEffect(() => {
        if (organizationID) {
            getEmployees({ variables: { agency_id: parseInt(organizationID) } });
        }
    }, [organizationID]);

    // Map employees to options
    useEffect(() => {
        if (employeesData?.employees_by_agency_id?.employees) {
            setEmployeeOptions(
                employeesData.employees_by_agency_id.employees.map((emp: any) => {
                    const name = `${emp.first_name} ${emp.last_name}`;
                    const initials = `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`.toUpperCase();
                    return { key: Number(emp.employee_id), name, initials };
                })
            );
        }
    }, [employeesData]);

    // Map status options
    useEffect(() => {
        if (statusData?.agency_progress_statuses) {
            setProgressStatusOptions(
                statusData.agency_progress_statuses.map((s: any) => ({ key: s.value, name: s.label }))
            );
        }
    }, [statusData]);

    // Pre-fill form when agency data loads
    useEffect(() => {
        const agency = agencyData?.migration_agency_by_id?.agency;
        if (!agency) return;

        setValue("agency_name", agency.agency_name || "");
        setValue("new_agency_id", agency.new_agency_id?.toString() || "");
        setValue("old_agency_id", agency.old_agency_id?.toString() || "");
        setValue("total_clients", agency.total_clients ?? 0);
        setValue("total_caregivers", agency.total_caregivers ?? 0);
        setValue("total_employees", agency.total_employees ?? 0);
        setValue("total_prospects", agency.total_prospects ?? undefined);
        setValue("verification_working_days", agency.verification_working_days ?? 0);
        setValue("onboarding_date", agency.onboarding_date?.split("T")[0] || "");
        setValue("verification_start_date", agency.verification_start_date?.split("T")[0] || "");
        setValue("data_migration_date", agency.data_migration_date?.split("T")[0] || "");

        if (agency.progress_status) {
            setSelectedStatus(agency.progress_status);
        }
        if (agency.agency_type) {
            setAgencyType(agency.agency_type);
        }

        if (agency.cx_member) {
            setCxMember1(agency.cx_member);
            // resolve label from employee list after options load
        }
        if (agency.cx_member_2) {
            setCxMember2(agency.cx_member_2);
        }

        // Pre-select assigned members — normalize to Number to avoid string/number mismatch
        if (agency.assigned_team_members?.length) {
            setAssignedMemberIds(agency.assigned_team_members.map((m: any) => Number(m.member_id)));
        }
    }, [agencyData]);

    // Sync status label once options are loaded
    useEffect(() => {
        if (selectedStatus && progressStatusOptions.length) {
            const match = progressStatusOptions.find(o => o.key === selectedStatus);
            if (match) setSelectedStatusLabel(match.name);
        }
    }, [selectedStatus, progressStatusOptions]);

    // Resolve cx_member labels once employee options are loaded
    useEffect(() => {
        if (!employeeOptions.length) return;
        if (cxMember1 && !cxMember1Label) {
            const match = employeeOptions.find(e => String(e.key) === String(cxMember1));
            if (match) setCxMember1Label(match.name);
        }
        if (cxMember2 && !cxMember2Label) {
            const match = employeeOptions.find(e => String(e.key) === String(cxMember2));
            if (match) setCxMember2Label(match.name);
        }
    }, [employeeOptions]);

    const toggleMember = (id: number) => {
        const numId = Number(id);
        setAssignedMemberIds(prev =>
            prev.includes(numId) ? prev.filter(x => x !== numId) : [...prev, numId]
        );
    };

    const handleSync = async () => {
        const agency = agencyData?.migration_agency_by_id?.agency;
        const newAgencyId = agency?.new_agency_id;
        const oldA: any = getValues("old_agency_id");
        const migrationAgencyId = agency?.id;
        const agencyTypeValue = agencyType;
        if (!newAgencyId || !migrationAgencyId) {
            showError("Agency not loaded yet");
            return;
        }
        if (!agencyTypeValue) {
            showError("Agency type is not set. Please select an agency type first.");
            return;
        }
        setSyncing(true);
        try {
            // Step 1: run sync_agency_field_statuses and sync_entities in parallel
            const [r1, r2] = await Promise.all([
                syncAgencyFieldStatus({ variables: { agency_id: parseInt(migrationAgencyId) } }),
                syncEntities({ variables: { agency_id: parseInt(newAgencyId), entity_type: "ALL", agency_location_id: parseInt(oldA), agency_obj_id: parseInt(migrationAgencyId), agency_type: agencyTypeValue } }),
            ]);

            const agencyFieldResult = r1.data?.sync_agency_field_statuses;
            const entitiesResult = r2.data?.sync_entities;

            if (agencyFieldResult && agencyFieldResult.status_code !== 200 && agencyFieldResult.status_code !== 201) {
                showError(agencyFieldResult.message || "Sync agency field statuses failed");
                return;
            }
            if (entitiesResult && entitiesResult.status_code !== 200 && entitiesResult.status_code !== 201) {
                showError(entitiesResult.message || "Sync entities failed");
                return;
            }

            // Step 2: only call sync_entity_field_statuses if sync_entities succeeded
            const r3 = await syncEntitiesFieldStatus({ variables: { agency_id: parseInt(migrationAgencyId), agency_type: agencyTypeValue } });
            const entityFieldResult = r3.data?.sync_entity_field_statuses;
            if (entityFieldResult && entityFieldResult.status_code !== 200 && entityFieldResult.status_code !== 201) {
                showError(entityFieldResult.message || "Sync entity field statuses failed");
                return;
            }

            showSuccess("Data synced successfully");
            setSynced(true);
            onSyncSuccess?.();
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        } finally {
            setSyncing(false);
        }
    };

    const onSubmit = async (data: FormValues) => {
        if (!agencyType) {
            setAgencyTypeError("Agency type is required");
            return;
        }
        try {
            const input: any = {
                id: parseInt(agencyId),
                agency_name: data.agency_name,
                new_agency_id: parseInt(data.new_agency_id),
                total_clients: data.total_clients,
                total_caregivers: data.total_caregivers,
                total_employees: data.total_employees,
                verification_working_days: data.verification_working_days,
                agency_type: agencyType,
            };
            if (data.old_agency_id) input.old_agency_id = parseInt(data.old_agency_id);
            if (data.total_prospects != null) input.total_prospects = data.total_prospects;
            if (data.onboarding_date) input.onboarding_date = data.onboarding_date;
            if (data.verification_start_date) input.verification_start_date = data.verification_start_date;
            if (data.data_migration_date) input.data_migration_date = data.data_migration_date;
            if (selectedStatus) input.progress_status = selectedStatus;
            if (cxMember1) input.cx_member = parseInt(cxMember1);
            if (cxMember2) input.cx_member_2 = parseInt(cxMember2);

            const assigned_members = assignedMemberIds.map((id: any) => ({
                member_id: parseInt(id),
                assigned_by: parseInt(employeeID),
            }));

            const res = await updateAgency({ variables: { input, assigned_members } });
            const result = res.data?.create_migration_agency;
            if (result?.status_code === 200 || result?.status_code === 201) {
                showSuccess(result.message || "Agency updated successfully");
                onSaveSuccess?.();
            } else {
                showError(result?.message || "Something went wrong");
            }
        } catch (err) {
            if (err instanceof ApolloError) showError(err.message);
        }
    };

    if (agencyLoading || employeesLoading) return <div className="p-6"><Loader /></div>;

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 flex flex-col gap-6">
            {/* Agency Details */}
            <div>
                <h4 className="text-base font-bold text-gray-900 dark:text-white mb-4">Agency Details</h4>
                <div className="grid grid-cols-2 mq825:grid-cols-1 gap-4">
                    <InputsNew
                        label="Agency Name*"
                        contentPlaceholder="Enter agency name"
                        type="text"
                        register={register("agency_name")}
                        errorMessage={errors.agency_name}
                    />
                    <InputsNew
                        label="New Agency ID*"
                        contentPlaceholder="Enter new agency ID"
                        type="number"
                        register={register("new_agency_id")}
                        errorMessage={errors.new_agency_id}
                    />
                    <InputsNew
                        label="Old Agency ID"
                        contentPlaceholder="Enter old agency ID"
                        type="number"
                        register={register("old_agency_id")}
                        errorMessage={errors.old_agency_id}
                    />
                    <InputsNew
                        label="Total Clients*"
                        contentPlaceholder="0"
                        type="number"
                        register={register("total_clients")}
                        errorMessage={errors.total_clients}
                    />
                    <InputsNew
                        label="Total Caregivers*"
                        contentPlaceholder="0"
                        type="number"
                        register={register("total_caregivers")}
                        errorMessage={errors.total_caregivers}
                    />
                    <InputsNew
                        label="Total Employees*"
                        contentPlaceholder="0"
                        type="number"
                        register={register("total_employees")}
                        errorMessage={errors.total_employees}
                    />
                    <InputsNew
                        label="Total Prospects"
                        contentPlaceholder="0"
                        type="number"
                        register={register("total_prospects")}
                        errorMessage={errors.total_prospects}
                    />
                    <InputsNew
                        label="Data Migration Date"
                        contentPlaceholder=""
                        type="date"
                        register={register("data_migration_date")}
                        errorMessage={errors.data_migration_date}
                    />
                    <InputsNew
                        label="Onboarding Date"
                        contentPlaceholder=""
                        type="date"
                        register={register("onboarding_date")}
                        errorMessage={errors.onboarding_date}
                    />
                    <InputsNew
                        label="Verification Start Date"
                        contentPlaceholder=""
                        type="date"
                        register={register("verification_start_date")}
                        errorMessage={errors.verification_start_date}
                    />
                    <InputsNew
                        label="Verification Working Days*"
                        contentPlaceholder="0"
                        type="number"
                        register={register("verification_working_days")}
                        errorMessage={errors.verification_working_days}
                    />
                </div>
                <div className="mt-4">
                    <SearchableInputsDropdown
                        label="Progress Status"
                        placeHolder="Select status"
                        options={progressStatusOptions}
                        initialValue={selectedStatusLabel}
                        onSelect={(key: string, name: string) => {
                            setSelectedStatus(key);
                            setSelectedStatusLabel(name);
                        }}
                        showSearchable={false}
                        dontselectlastValue={true}
                    />
                </div>
                <div className="mt-4">
                    <SearchableInputsDropdown
                        label="Agency Type*"
                        placeHolder="Select agency type"
                        options={[
                            { key: "home_care", name: "Home Care" },
                            { key: "case_management", name: "Case Management" },
                        ]}
                        initialValue={agencyType === "home_care" ? "Home Care" : agencyType === "case_management" ? "Case Management" : ""}
                        onSelect={(key: string) => {
                            setAgencyType(key);
                            setAgencyTypeError("");
                        }}
                        showSearchable={false}
                        dontselectlastValue={true}
                    />
                    {agencyTypeError && (
                        <p className="text-red-500 text-xs mt-1">{agencyTypeError}</p>
                    )}
                </div>
                <div className="grid grid-cols-2 mq825:grid-cols-1 gap-4 mt-4">
                    <SearchableInputsDropdown
                        label="Customer Support 1"
                        placeHolder="Select member"
                        options={employeeOptions.map(e => ({ key: String(e.key), name: e.name }))}
                        initialValue={cxMember1Label}
                        onSelect={(key: string, name: string) => {
                            setCxMember1(key);
                            setCxMember1Label(name);
                        }}
                        showSearchable={true}
                        dontselectlastValue={true}
                    />
                    <SearchableInputsDropdown
                        label="Customer Support 2"
                        placeHolder="Select member"
                        options={employeeOptions.map(e => ({ key: String(e.key), name: e.name }))}
                        initialValue={cxMember2Label}
                        onSelect={(key: string, name: string) => {
                            setCxMember2(key);
                            setCxMember2Label(name);
                        }}
                        showSearchable={true}
                        dontselectlastValue={true}
                    />
                </div>
            </div>

            {/* Assign Team Members */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h4 className="text-base font-bold text-gray-900 dark:text-white mb-2">Assign Team Members</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Select employees who will be involved in migrating this agency
                </p>
                <div className="relative mb-4">
                    <input
                        type="text"
                        placeholder="Search members..."
                        className="w-full px-4 py-2 pl-9 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                        value={memberSearch}
                        onChange={e => setMemberSearch(e.target.value)}
                    />
                    <svg className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                <div className="grid grid-cols-2 mq825:grid-cols-1 gap-3">
                    {employeeOptions
                        .filter(emp => emp.name.toLowerCase().includes(memberSearch.toLowerCase()))
                        .map(emp => (
                        <div
                            key={emp.key}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                assignedMemberIds.includes(Number(emp.key))
                                    ? "bg-blue-50 border-blue-500 dark:bg-blue-900/30"
                                    : "bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:border-gray-300"
                            }`}
                            onClick={() => toggleMember(emp.key)}
                        >
                            <input
                                type="checkbox"
                                className="w-4 h-4 cursor-pointer"
                                checked={assignedMemberIds.includes(Number(emp.key))}
                                onChange={() => toggleMember(emp.key)}
                                onClick={e => e.stopPropagation()}
                            />
                            <div className="w-9 h-9 rounded-full bg-blue-600 text-white text-xs font-semibold flex items-center justify-center shrink-0">
                                {emp.initials}
                            </div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white m-0">{emp.name}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
                <button
                    type="button"
                    disabled={syncing || agencyLoading}
                    onClick={handleSync}
                    className={`px-6 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors disabled:opacity-60 ${
                        synced
                            ? "bg-green-600 hover:bg-green-700 text-white"
                            : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                >
                    {syncing ? (
                        <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                            Syncing...
                        </>
                    ) : synced ? (
                        <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Synced
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Sync Data
                        </>
                    )}
                </button>
                <button
                    type="submit"
                    disabled={saving || (!synced && selectedStatus !== "data_verified" && selectedStatus !== "first_billing_completed")}
                    title={(!synced && selectedStatus !== "data_verified" && selectedStatus !== "first_billing_completed") ? "Please sync data before saving" : undefined}
                    className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {saving ? "Saving..." : "Save Changes"}
                </button>
            </div>
        </form>
    );
};

export default AgencyConfigTab;
